import { H3Error } from 'h3'
import { FetchError as OfetchError } from 'ofetch'

import type { Owner } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { FrameworkError } from './useErrorLogger/FrameworkError'
import { HttpError } from './useErrorLogger/HttpError'
import { ImplementationError } from './useErrorLogger/ImplementationError'
import type { ParsedError } from './useErrorLogger/ParsedError'

export function useErrorLogger(feature: string, owners: Array<Owner>) {
  const logger = useLogger()

  return function log(
    message: string,
    error?: unknown,
    customAttributes: Record<string, unknown> = {},
  ): void {
    const parsedError = parseError(error, customAttributes)

    const logData = {
      owners,
      feature,
      tag: parsedError.tag,
      ...(error instanceof Error ? { error } : {}),
      ...parsedError.attributes,
    }

    logger.error(message, logData)
  }
}

function parseError(
  error: unknown,
  customAttributes: Record<string, unknown>,
): ParsedError {
  if (!error) {
    return new ImplementationError(customAttributes)
  }

  if (isWrappedHttpError(error)) {
    return parseHttpError(error.cause, customAttributes)
  }

  if (error instanceof OfetchError) {
    return parseHttpError(error, customAttributes)
  }

  if (error instanceof H3Error) {
    return new FrameworkError(
      error.statusCode,
      error.statusMessage,
      customAttributes,
    )
  }

  return new ImplementationError(customAttributes)
}

function isWrappedHttpError(error: unknown): error is H3Error & {
  cause: OfetchError
} {
  return error instanceof H3Error && error.cause instanceof OfetchError
}

function parseHttpError(
  error: OfetchError,
  customAttributes: Record<string, unknown>,
): HttpError {
  const { status, statusMessage, response } = error

  return new HttpError(status, statusMessage, {
    ...customAttributes,
    response,
  })
}
